<script setup lang="ts">
	import { computed, ref } from 'vue'

	import useCompanyFlightRequests from '@/modules/companies/composables/useCompanyFlightRequests'

	interface Props {
		companyId?: number
		dateRange?: string[] | null
		creationDateRange?: string[] | null
		statusFilter?: string
		datePreset?: string
		creationDatePreset?: string
		searchTravelerId?: number | null
		createdAt?: string[] | null
		orderBy?: string
		orderDirection?: 'asc' | 'desc'
	}

	const props = withDefaults(defineProps<Props>(), {
		statusFilter: 'all',
		searchTravelerId: null,
	})

	const currentPage = ref(1)
	const pageSize = ref(10)

	const { requests, meta, isLoading } = useCompanyFlightRequests(
		computed(() => ({
			companyId: props.companyId,
			page: currentPage.value,
			perPage: pageSize.value,
			filters: {
				dates: props.dateRange,
				creationDates: props.creationDateRange,
				status: props.statusFilter !== 'all' ? props.statusFilter : undefined,
				travelerId: props.searchTravelerId || undefined,
				createdAt: props.createdAt || undefined,
				orderBy: props.orderBy || undefined,
				orderDirection: props.orderDirection || undefined,
			},
		}))
	)

	const formatCurrency = (value: string | number) => {
		const numValue = typeof value === 'string' ? parseFloat(value) : value
		return new Intl.NumberFormat('es-MX', {
			style: 'currency',
			currency: 'MXN',
			signDisplay: 'never',
		}).format(numValue)
	}

	const formatDate = (date: string) => {
		return new Date(date).toLocaleDateString('es-MX', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		})
	}

	const getStateBadgeClass = (stateName: string) => {
		const stateClasses: Record<string, string> = {
			Confirmado: 'bg-green-100 text-green-800',
			Pendiente: 'bg-yellow-100 text-yellow-800',
			Cancelado: 'bg-red-100 text-red-800',
			Pagado: 'bg-blue-100 text-blue-800',
		}
		return stateClasses[stateName] || 'bg-gray-100 text-gray-800'
	}

	const getCabinLabel = (cabin: string | undefined) => {
		if (!cabin) return '—'
		const labels: Record<string, string> = {
			Y: 'Económica',
			W: 'Eco Premium',
			C: 'Business',
			F: 'Primera',
			S: 'Eco Premium',
			J: 'Business',
		}
		return labels[cabin] || cabin
	}

	const formatRoute = (request: any) => {
		const origin =
			request.originAirport?.iataCode || request.origin || '—'
		const destination =
			request.destinationAirport?.iataCode ||
			request.destination ||
			'—'
		return `${origin} → ${destination}`
	}

	const formatRouteDetail = (request: any) => {
		const originCity =
			request.originAirport?.cityName || ''
		const destCity =
			request.destinationAirport?.cityName || ''
		if (originCity && destCity) return `${originCity} — ${destCity}`
		return ''
	}

	const goToPage = (page: number) => {
		currentPage.value = page
	}
</script>

<template>
	<div class="w-full space-y-6">
		<!-- Vista movil (cards) -->
		<div class="lg:hidden space-y-4">
			<!-- Loading state movil -->
			<div
				v-if="isLoading"
				class="bg-white rounded-lg shadow p-6"
			>
				<div class="flex justify-center items-center">
					<svg
						class="animate-spin h-8 w-8 text-blue-500"
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
					>
						<circle
							class="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							stroke-width="4"
						></circle>
						<path
							class="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						></path>
					</svg>
					<span class="ml-3 text-gray-500">Cargando...</span>
				</div>
			</div>

			<!-- Empty state movil -->
			<div
				v-else-if="requests.length === 0"
				class="bg-white rounded-lg shadow p-8"
			>
				<div class="flex flex-col items-center">
					<span class="material-symbols-outlined text-5xl text-gray-400 mb-3">
						flight
					</span>
					<p class="text-gray-500 text-lg font-medium text-center">
						No hay vuelos disponibles
					</p>
					<p class="text-gray-400 text-sm mt-1 text-center">
						Ajusta los filtros para ver mas resultados
					</p>
				</div>
			</div>

			<!-- Cards movil -->
			<div
				v-else
				v-for="request in requests"
				:key="request.id"
				class="rounded-lg shadow overflow-hidden bg-white"
			>
				<div class="p-4 space-y-3">
					<!-- Header con estado -->
					<div class="flex items-start justify-between">
						<div class="flex-1 min-w-0">
							<p class="text-sm font-semibold text-gray-900 truncate">
								{{ request.travelerName }}
							</p>
							<p class="text-xs text-gray-500 mt-0.5 capitalize truncate">
								{{ request.suppliableName }}
							</p>
						</div>
						<span
							:class="getStateBadgeClass(request.stateName)"
							class="px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ml-2"
						>
							{{ request.stateName }}
						</span>
					</div>

					<!-- Ruta -->
					<div
						class="flex items-center gap-2 bg-blue-50 rounded-lg p-2"
					>
						<span class="material-symbols-outlined text-blue-600 text-lg">
							flight_takeoff
						</span>
						<div class="min-w-0">
							<p class="text-sm font-medium text-blue-900">
								{{ formatRoute(request) }}
							</p>
							<p
								v-if="formatRouteDetail(request)"
								class="text-xs text-blue-600 truncate"
							>
								{{ formatRouteDetail(request) }}
							</p>
						</div>
					</div>

					<!-- Info principal -->
					<div class="space-y-2 border-t border-gray-100 pt-3">
						<div class="flex items-center justify-between text-sm">
							<span class="text-gray-500">Salida:</span>
							<span class="text-gray-900 font-medium">{{
								formatDate(request.startDate)
							}}</span>
						</div>
						<div
							v-if="request.endDate"
							class="flex items-center justify-between text-sm"
						>
							<span class="text-gray-500">Regreso:</span>
							<span class="text-gray-900 font-medium">{{
								formatDate(request.endDate)
							}}</span>
						</div>
						<div
							v-if="request.pnr"
							class="flex items-center justify-between text-sm"
						>
							<span class="text-gray-500">PNR:</span>
							<span
								class="text-gray-900 font-mono font-medium"
							>{{ request.pnr }}</span>
						</div>
						<div class="flex items-center justify-between text-sm">
							<span class="text-gray-500">Clase:</span>
							<span class="text-gray-900 font-medium">{{
								getCabinLabel(request.flightDetails?.cabinClass)
							}}</span>
						</div>
						<div class="flex items-center justify-between text-sm">
							<span class="text-gray-500">Total:</span>
							<span class="text-gray-900 font-bold text-base">
								{{ formatCurrency(request.total) }}
								<span class="text-[10px] font-normal text-gray-400"
									>MXN</span
								>
							</span>
						</div>
					</div>

					<!-- Footer -->
					<div class="border-t border-gray-100 pt-3 space-y-2">
						<div class="flex items-center justify-between text-xs">
							<span class="text-gray-500">Creado:</span>
							<span class="text-gray-700">
								{{ formatDate(request.createdAt) }}
							</span>
						</div>
						<div class="flex items-center justify-between text-xs">
							<span class="text-gray-500">Reservado por:</span>
							<div class="flex items-center gap-1.5">
								<img
									v-if="request.creator?.internal"
									src="@/assets/imgs/icono_enlace.png"
									class="!w-4"
									alt=""
								/>
								<span class="text-gray-700 truncate max-w-[150px]">
									{{ request.creator?.name }}
									{{ request.creator?.lastname }}
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Vista desktop (tabla) -->
		<div class="hidden lg:block overflow-x-auto bg-white rounded-lg shadow">
			<table class="w-full table-fixed">
				<thead class="bg-gray-50 border-b border-gray-200">
					<tr>
						<th
							class="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[11%]"
						>
							Viajero
						</th>
						<th
							class="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[10%]"
						>
							Aerolínea
						</th>
						<th
							class="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[13%]"
						>
							Ruta
						</th>
						<th
							class="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[9%]"
						>
							Salida
						</th>
						<th
							class="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[9%]"
						>
							Regreso
						</th>
						<th
							class="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[8%]"
						>
							PNR
						</th>
						<th
							class="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[8%]"
						>
							Clase
						</th>
						<th
							class="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[9%]"
						>
							Total
						</th>
						<th
							class="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[8%]"
						>
							Estatus
						</th>
						<th
							class="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[9%]"
						>
							Creación
						</th>
						<th
							class="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[9%]"
						>
							Reservado por
						</th>
					</tr>
				</thead>
				<tbody class="divide-y divide-gray-200">
					<!-- Loading -->
					<tr v-if="isLoading">
						<td
							colspan="11"
							class="px-4 py-12 text-center"
						>
							<div class="flex justify-center items-center">
								<svg
									class="animate-spin h-8 w-8 text-blue-500"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										class="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										stroke-width="4"
									></circle>
									<path
										class="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									></path>
								</svg>
								<span class="ml-3 text-gray-500">Cargando...</span>
							</div>
						</td>
					</tr>

					<!-- Empty -->
					<tr v-else-if="requests.length === 0">
						<td
							colspan="11"
							class="px-4 py-12 text-center"
						>
							<div class="flex flex-col items-center">
								<span
									class="material-symbols-outlined text-5xl text-gray-400 mb-3"
								>
									flight
								</span>
								<p class="text-gray-500 text-lg font-medium">
									No hay vuelos disponibles
								</p>
								<p class="text-gray-400 text-sm mt-1">
									Ajusta los filtros para ver mas resultados
								</p>
							</div>
						</td>
					</tr>

					<!-- Data rows -->
					<tr
						v-else
						v-for="request in requests"
						:key="request.id"
						class="hover:bg-gray-50 border-b border-gray-100 transition-colors"
					>
						<td class="px-3 py-3 text-sm text-gray-900">
							<span
								v-tooltip="request.travelerName"
								class="block truncate"
							>
								{{ request.travelerName }}
							</span>
						</td>
						<td class="px-3 py-3 text-sm font-medium text-gray-900 capitalize">
							<span
								v-tooltip="request.suppliableName"
								class="block truncate"
							>
								{{ request.suppliableName }}
							</span>
						</td>
						<td class="px-3 py-3 text-sm text-gray-900">
							<div class="flex items-center gap-1.5">
								<span
									class="material-symbols-outlined text-blue-500 text-base"
								>
									flight_takeoff
								</span>
								<div class="min-w-0">
									<span
										v-tooltip="formatRouteDetail(request)"
										class="block truncate font-medium"
									>
										{{ formatRoute(request) }}
									</span>
									<span
										v-if="formatRouteDetail(request)"
										class="block text-xs text-gray-500 truncate"
									>
										{{ formatRouteDetail(request) }}
									</span>
								</div>
							</div>
						</td>
						<td class="px-3 py-3 text-sm text-gray-900">
							{{ formatDate(request.startDate) }}
						</td>
						<td class="px-3 py-3 text-sm text-gray-900">
							{{ request.endDate ? formatDate(request.endDate) : '—' }}
						</td>
						<td class="px-3 py-3 text-sm text-gray-900">
							<span
								v-if="request.pnr"
								class="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded"
							>
								{{ request.pnr }}
							</span>
							<span
								v-else
								class="text-gray-400"
							>—</span>
						</td>
						<td class="px-3 py-3 text-sm text-gray-900">
							{{ getCabinLabel(request.flightDetails?.cabinClass) }}
						</td>
						<td class="px-3 py-3 text-sm font-semibold text-gray-900">
							{{ formatCurrency(request.total) }}
							<span class="text-[10px] font-normal text-gray-400">MXN</span>
						</td>
						<td class="px-3 py-3 text-sm">
							<span
								:class="getStateBadgeClass(request.stateName)"
								class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
							>
								{{ request.stateName }}
							</span>
						</td>
						<td class="px-3 py-3 text-xs text-gray-900">
							<div class="flex flex-col gap-0.5">
								<span class="font-medium">{{
									formatDate(request.createdAt)
								}}</span>
								<span class="text-gray-500">{{
									new Date(request.createdAt).toLocaleTimeString('es-MX', {
										hour: '2-digit',
										minute: '2-digit',
									})
								}}</span>
							</div>
						</td>
						<td class="px-3 py-3 text-sm text-gray-600">
							<div class="flex items-center justify-center gap-2">
								<div v-if="request.creator?.internal">
									<img
										src="@/assets/imgs/icono_enlace.png"
										class="!w-5"
										alt=""
									/>
								</div>
								<p
									v-tooltip="
										`${request.creator?.name} ${request.creator?.lastname}`
									"
									class="truncate max-w-[100px]"
								>
									{{ request.creator?.name }}
									{{ request.creator?.lastname }}
								</p>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- Paginacion -->
		<div
			v-if="meta && meta.total > 0"
			class="mt-6 flex items-center justify-between bg-white px-4 py-5 sm:px-6 rounded-lg shadow-sm border border-gray-200"
		>
			<div class="sm:hidden w-full">
				<div class="flex flex-col items-center gap-4 mb-4">
					<p class="text-sm text-gray-700 text-center">
						<span class="font-medium">{{
							(currentPage - 1) * pageSize + 1
						}}</span>
						-
						<span class="font-medium">{{
							Math.min(currentPage * pageSize, meta.total)
						}}</span>
						de
						<span class="font-medium">{{ meta.total }}</span>
					</p>
					<div class="flex items-center gap-2">
						<label
							for="perPageMobile"
							class="text-sm text-gray-700"
						>
							Por pagina:
						</label>
						<select
							id="perPageMobile"
							v-model="pageSize"
							@change="currentPage = 1"
							class="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
						>
							<option :value="5">5</option>
							<option :value="10">10</option>
							<option :value="25">25</option>
							<option :value="50">50</option>
							<option :value="100">100</option>
						</select>
					</div>
				</div>
				<div class="flex justify-center gap-3">
					<button
						@click="goToPage(currentPage - 1)"
						:disabled="currentPage === 1"
						class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						Anterior
					</button>
					<button
						@click="goToPage(currentPage + 1)"
						:disabled="currentPage === meta.lastPage"
						class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						Siguiente
					</button>
				</div>
			</div>
			<div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
				<div class="flex items-center gap-4">
					<p class="text-sm font-medium text-gray-900">
						Mostrando
						<span class="font-bold text-blue-600">{{
							(currentPage - 1) * pageSize + 1
						}}</span>
						a
						<span class="font-bold text-blue-600">{{
							Math.min(currentPage * pageSize, meta.total)
						}}</span>
						de
						<span class="font-bold text-blue-600">{{ meta.total }}</span>
						vuelos
					</p>
					<div class="flex items-center gap-2">
						<label
							for="perPage"
							class="text-sm font-medium text-gray-700"
						>
							Por pagina:
						</label>
						<select
							id="perPage"
							v-model="pageSize"
							@change="currentPage = 1"
							class="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
						>
							<option :value="5">5</option>
							<option :value="10">10</option>
							<option :value="25">25</option>
							<option :value="50">50</option>
							<option :value="100">100</option>
						</select>
					</div>
				</div>
				<div>
					<nav
						class="isolate inline-flex -space-x-px rounded-md shadow-sm"
						aria-label="Pagination"
					>
						<button
							@click="goToPage(currentPage - 1)"
							:disabled="currentPage === 1"
							class="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<span class="sr-only">Anterior</span>
							<svg
								class="h-5 w-5"
								viewBox="0 0 20 20"
								fill="currentColor"
								aria-hidden="true"
							>
								<path
									fill-rule="evenodd"
									d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
									clip-rule="evenodd"
								/>
							</svg>
						</button>

						<template
							v-for="page in meta.lastPage"
							:key="page"
						>
							<button
								v-if="
									page === 1 ||
									page === meta.lastPage ||
									(page >= currentPage - 2 && page <= currentPage + 2)
								"
								@click="goToPage(page)"
								:class="[
									page === currentPage ?
										'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
									:	'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0',
									'relative inline-flex items-center px-4 py-2 text-sm font-semibold',
								]"
							>
								{{ page }}
							</button>
							<span
								v-else-if="
									page === currentPage - 3 ||
									(page === currentPage + 3 && page < meta.lastPage)
								"
								class="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
							>
								...
							</span>
						</template>

						<button
							@click="goToPage(currentPage + 1)"
							:disabled="currentPage === meta.lastPage"
							class="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<span class="sr-only">Siguiente</span>
							<svg
								class="h-5 w-5"
								viewBox="0 0 20 20"
								fill="currentColor"
								aria-hidden="true"
							>
								<path
									fill-rule="evenodd"
									d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
									clip-rule="evenodd"
								/>
							</svg>
						</button>
					</nav>
				</div>
			</div>
		</div>
	</div>
</template>
